// src/components/adjustments/GenerateThirteenthMonthModal.js
//
// The 13th month generator (PRD_ThirteenthMonthPay C1/C12/C13/C15/C16/C17). Loads the
// year's approved payroll lines, shows one row per employee with a computed,
// editable To pay, and hands the ticked rows to the page, which creates
// ordinary `thirteenth_month` adjustments (createAdjustmentsPerRow) and shows
// the same toasts as Bulk Add. Every amount comes from thirteenthMonthService's
// pure core; this file only holds the dialog state.

import React, { useEffect, useId, useMemo, useState } from "react";
import Modal from "../modals/Modal";
import Icon from "../icons/Icon";
import PwPopover from "../PwPopover";
import { matchesEmployeeSearch } from "../../utils/employeeSearch";
import { formatPeso } from "../../utils/currency";
import { currentPeriod } from "../../utils/payCutoffs";
import {
  computeThirteenthMonthRows,
  defaultYearAndDate,
  placeDefaultDate,
  isTickable,
  isDefaultTicked,
  loadThirteenthMonthInputs,
} from "../../services/thirteenthMonthService";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dMMM = (ymd) => `${Number(ymd.slice(8, 10))} ${MONTHS[Number(ymd.slice(5, 7)) - 1]}`;
const dMMMyyyy = (ymd) => `${dMMM(ymd)} ${ymd.slice(0, 4)}`;

const inputStyle = {
  padding: "0.5rem 0.75rem",
  border: "1px solid var(--ui-border)",
  borderRadius: "6px",
  fontSize: "0.875rem",
};
const label = { fontSize: "0.75rem", fontWeight: 600, color: "var(--ui-text-secondary)", marginBottom: "0.25rem", display: "block" };
// The list-page header look (DESIGN-STANDARDS.md; AdjustmentsPage's `th`) on the brand gradient.
const th = {
  padding: "10px 8px", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px",
  color: "white", textAlign: "right", whiteSpace: "nowrap", userSelect: "none",
};
const td = { padding: "0.5rem", fontSize: "0.8125rem", textAlign: "right", fontVariantNumeric: "tabular-nums", verticalAlign: "top" };
// C12 chip tones: amber = the row cannot take an entry, red = needs action, grey = information.
const CHIP_TONE = {
  block: { background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)" },
  action: { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)" },
  info: { background: "var(--ui-surface-recessed)", color: "var(--ui-text-secondary)" },
};
const chipStyle = {
  display: "inline-block", padding: "0.125rem 0.5rem", borderRadius: "10px",
  fontSize: "0.6875rem", fontWeight: 600, whiteSpace: "nowrap",
};

// Sort icon + sortable header — the AdjustmentsPage pattern (C16).
const SortIcon = ({ field, sortField, sortDirection }) => {
  const isActive = sortField === field;
  const base = { marginLeft: "4px", verticalAlign: "middle" };
  if (isActive && sortDirection === "asc") return <Icon name="arrow-up" size={12} style={base} />;
  if (isActive && sortDirection === "desc") return <Icon name="arrow-down" size={12} style={base} />;
  return <Icon name="chevrons-up-down" size={12} style={{ ...base, opacity: 0.5 }} />;
};
const SortableTh = ({ field, label: text, style, sortField, sortDirection, onSort }) => {
  const [hover, setHover] = useState(false);
  return (
    <th
      onClick={() => onSort(field)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ ...style, cursor: "pointer", background: hover ? "rgba(255,255,255,0.1)" : undefined }}
    >
      {text}
      <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
    </th>
  );
};

// C17 — what each money column means. Clickable (PwPopover), never hover-only.
const COLUMN_DEFINITIONS = [
  ["Basis earned", "Basic pay in this year's approved payroll runs in Sahod, adjusted by the 13th Month Basis on Payroll Rates. Pay from before the company used Sahod isn't included — add it to To pay."],
  ["Projected", "Estimated basic pay for the rest of the year not yet approved. December dates and leavers only."],
  ["13th month", "(Basis earned + Projected) ÷ 12."],
  ["Already given", "13th month this employee already received or was allotted this year (e.g. a half paid in June, or an earlier generation). It's subtracted so no one is paid twice."],
  ["To pay", "13th month minus Already given. You can change it before creating."],
];

// C12 — a short label per note, on the line below the name; the full sentence opens on click.
const rowNotes = (row, year) => {
  const out = [];
  const b = row.blockReason;
  if (b?.code === "separated") {
    out.push({ label: "Separated", tone: "block", text: `Separated on ${dMMMyyyy(b.date)} — pick a date in their final run` });
  } else if (b?.code === "notYetHired") {
    out.push({ label: "Not yet hired", tone: "block", text: `Starts ${dMMMyyyy(b.date)} — not employed on this date` });
  } else if (b?.code === "alreadyPaid") {
    out.push({ label: "Cycle closed", tone: "block", text: "This date is already paid for this employee — pick a date in an open cycle" });
  } else if (row.noApprovedRun) {
    out.push({ label: "No approved run", tone: "info", text: `No approved pay run in ${year} — enter the amount` });
  } else if (row.computedToPay === 0) {
    out.push({ label: "Nothing to pay", tone: "info", text: `Nothing left to pay for ${year}` });
  }
  row.stuck.forEach((s) =>
    out.push({
      label: "Unpaid entry",
      tone: "action",
      text: `${formatPeso(s.amount)} dated ${dMMM(s.effectiveDate)} can't be paid (that cycle is closed) — void or re-date it`,
    }));
  if (row.lateFirstRun) {
    out.push({ label: "Late first run", tone: "info", text: `First approved run starts ${dMMM(row.lateFirstRun)} — add earlier pay to To pay` });
  }
  return out;
};

const GenerateThirteenthMonthModal = ({ companyId, employees, cutoffStartDays, dateClamp, todayYMD, saving, onClose, onSave }) => {
  const titleId = useId();
  const [initial] = useState(() => defaultYearAndDate({ todayYMD, cutoffStartDays }));
  const [year, setYear] = useState(initial.year);
  const [effectiveDate, setEffectiveDate] = useState(initial.effectiveDate);
  const [inputs, setInputs] = useState(null);
  const [loadState, setLoadState] = useState("loading"); // loading | error | ready
  const [amounts, setAmounts] = useState({}); // employeeId → typed string (C13)
  const [ticked, setTicked] = useState(() => new Set());
  // C16 — review aids. They narrow and order the VIEW only: a ticked row that a
  // filter hides is still created and still counted (as Bulk Add's search does).
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  // Opens on "Can be paid": admins mostly want the people they can pay.
  const [show, setShow] = useState("payable"); // all | payable
  const [sortField, setSortField] = useState("employee");
  const [sortDirection, setSortDirection] = useState("asc");

  const departmentById = useMemo(
    () => Object.fromEntries((employees || []).map((e) => [String(e.employeeId), e.department || ""])),
    [employees],
  );
  const departments = useMemo(
    () => [...new Set((employees || []).map((e) => e.department).filter(Boolean))].sort(),
    [employees],
  );

  const rows = useMemo(
    () => (inputs ? computeThirteenthMonthRows({ year, effectiveDate, employees, ...inputs }) : []),
    [inputs, year, effectiveDate, employees],
  );
  const amountFrom = (typed, row) =>
    typed[row.employeeId] !== undefined ? Number(typed[row.employeeId]) : row.computedToPay;
  const amountOf = (row) => amountFrom(amounts, row);
  const canTick = (row) => isTickable(row, amountOf(row));
  // C15 — default ticks judged on the amount as entered, so a date change never
  // drops a row whose To pay the admin typed.
  const deriveTicks = (rs, date, y, typed = {}) =>
    new Set(rs.filter((r) => isDefaultTicked(r, date, y, amountFrom(typed, r))).map((r) => r.employeeId));

  // Load on open and on Year change; a Year change discards typed amounts and ticks.
  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    loadThirteenthMonthInputs(companyId, year)
      .then((loaded) => {
        if (cancelled) return;
        setInputs(loaded);
        setAmounts({});
        setTicked(deriveTicks(
          computeThirteenthMonthRows({ year, effectiveDate, employees, ...loaded }), effectiveDate, year,
        ));
        setLoadState("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[GenerateThirteenthMonthModal] load failed:", err);
        setLoadState("error");
      });
    return () => {
      cancelled = true;
    };
    // effectiveDate is read once per load; a date change recomputes without reloading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, year]);

  // A Year change discards typed amounts and ticks at once — not on load — so
  // nothing from the previous year can be created while the new one loads.
  const changeYear = (y) => {
    setAmounts({});
    setTicked(new Set());
    setLoadState("loading");
    setYear(y);
    setEffectiveDate(placeDefaultDate(y, cutoffStartDays, todayYMD));
  };
  // A date change keeps typed amounts and re-derives ticks (C15).
  const changeDate = (date) => {
    setEffectiveDate(date);
    if (inputs) {
      setTicked(deriveTicks(
        computeThirteenthMonthRows({ year, effectiveDate: date, employees, ...inputs }), date, year, amounts,
      ));
    }
  };
  const toggle = (id) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onSort = (field) => {
    if (field === sortField) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };
  const SORT_KEY = {
    employee: (r) => r.fullName,
    approvedBasis: (r) => r.approvedBasis,
    projected: (r) => r.projected,
    thirteenth: (r) => r.thirteenth,
    alreadyGiven: (r) => r.alreadyGiven,
    // The calculated figure, not the typed one: a row must not move while its box is edited.
    toPay: (r) => r.computedToPay,
  };
  const visible = rows
    .filter((r) => matchesEmployeeSearch(r, search))
    .filter((r) => !department || departmentById[r.employeeId] === department)
    // Judged on the calculated amount, so clearing a box to retype never hides the row mid-edit.
    .filter((r) => show === "all" || (!r.blockReason && r.computedToPay > 0))
    .sort((a, b) => {
      const ka = SORT_KEY[sortField](a);
      const kb = SORT_KEY[sortField](b);
      const c = typeof ka === "string" ? ka.localeCompare(kb) : ka - kb;
      return (sortDirection === "asc" ? c : -c) || a.employeeId.localeCompare(b.employeeId);
    });
  // Select-all acts on the rows in view; ticks on hidden rows are left alone.
  const tickable = visible.filter(canTick);
  // Nothing is creatable until the year has loaded and a date is chosen.
  const ready = loadState === "ready" && Boolean(effectiveDate);
  const chosen = ready ? rows.filter((r) => ticked.has(r.employeeId) && canTick(r)) : [];
  const allOn = tickable.length > 0 && tickable.every((r) => ticked.has(r.employeeId));
  const total = chosen.reduce((s, r) => s + amountOf(r), 0);
  const anyProjected = rows.some((r) => r.projected > 0);
  const period = effectiveDate ? currentPeriod(cutoffStartDays, effectiveDate) : null;
  // Shown for December dates only: a January top-up is the intended catch-up.
  const pastDeadline =
    Boolean(period) && effectiveDate.slice(0, 7) === `${year}-12` && period.end > `${year}-12-24`;
  const years = [Number(todayYMD.slice(0, 4)), Number(todayYMD.slice(0, 4)) - 1];

  const submit = () =>
    onSave(
      chosen.map((r) => ({ employeeId: r.employeeId, amount: amountOf(r) })),
      {
        type: "earning",
        category: "thirteenth_month",
        description: `13th Month Pay ${year}`,
        effectiveDate,
        thirteenthMonthYear: year,
      },
    );

  return (
    <Modal
      isOpen
      onClose={onClose}
      busy={saving}
      labelledBy={titleId}
      style={{ background: "var(--ui-surface)", borderRadius: "12px", maxWidth: "960px", width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
    >
      <div style={{ padding: "1.5rem", overflowY: "auto" }}>
        <h3 id={titleId} style={{ margin: "0 0 1rem", fontSize: "1rem", color: "var(--ui-text)" }}>Generate 13th Month Pay</h3>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginBottom: "0.75rem" }}>
          <div>
            <label style={label} htmlFor={`${titleId}-year`}>Year</label>
            <select id={`${titleId}-year`} value={year} onChange={(e) => changeYear(Number(e.target.value))} style={inputStyle}>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label} htmlFor={`${titleId}-date`}>Effective date</label>
            <input
              id={`${titleId}-date`}
              type="date"
              value={effectiveDate}
              min={dateClamp.min}
              max={dateClamp.max}
              onChange={(e) => changeDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {loadState === "loading" && (
          <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--ui-text-muted)" }}>Loading payroll runs…</div>
        )}
        {loadState === "error" && (
          <div role="alert" style={{ background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)", borderRadius: "8px", padding: "0.625rem 0.875rem", fontSize: "0.8125rem" }}>
            Couldn't load payroll runs. Nothing was created — try again.
          </div>
        )}

        {loadState === "ready" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem", color: "var(--ui-text-secondary)", marginBottom: "0.5rem" }}>
              <span>
                {anyProjected
                  ? `Projected = basic per day so far × the days left in ${year}, up to each employee's last day.`
                  : "Amounts cover approved earnings to date only."}
              </span>
              <PwPopover
                ariaLabel="Column definitions"
                strategy="fixed"
                align="end"
                trigger={<Icon name="info" size={16} style={{ color: "var(--ui-primary)", verticalAlign: "middle" }} />}
              >
                {COLUMN_DEFINITIONS.map(([term, text]) => (
                  <div key={term} style={{ marginBottom: "0.375rem" }}>
                    <strong>{term}</strong> — {text}
                  </div>
                ))}
              </PwPopover>
            </div>
            {pastDeadline && (
              <div style={{ background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)", borderRadius: "8px", padding: "0.5rem 0.875rem", fontSize: "0.8125rem", marginBottom: "0.5rem" }}>
                This pay cycle ends after 24 December — PD 851 requires 13th month pay by then.
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <input
                type="text"
                aria-label="Search employees"
                placeholder="Search employee name or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, flex: 1, minWidth: "180px" }}
              />
              <select aria-label="Department" value={department} onChange={(e) => setDepartment(e.target.value)} style={{ ...inputStyle, minWidth: "150px" }}>
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select aria-label="Show" value={show} onChange={(e) => setShow(e.target.value)} style={{ ...inputStyle, minWidth: "150px" }}>
                <option value="all">All employees</option>
                <option value="payable">Can be paid</option>
              </select>
            </div>

            <div style={{ border: "1px solid var(--ui-border)", borderRadius: "8px", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--ui-gradient-primary)" }}>
                    <th style={{ ...th, textAlign: "left" }}>
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={allOn}
                        disabled={tickable.length === 0}
                        onChange={() =>
                          setTicked((prev) => {
                            const next = new Set(prev);
                            tickable.forEach((r) => (allOn ? next.delete(r.employeeId) : next.add(r.employeeId)));
                            return next;
                          })
                        }
                      />
                    </th>
                    <SortableTh field="employee" label="Employee" style={{ ...th, textAlign: "left" }} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                    <SortableTh field="approvedBasis" label="Basis earned" style={th} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                    <SortableTh field="projected" label="Projected" style={th} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                    <SortableTh field="thirteenth" label="13th month" style={th} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                    <SortableTh field="alreadyGiven" label="Already given" style={th} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                    <SortableTh field="toPay" label="To pay" style={th} sortField={sortField} sortDirection={sortDirection} onSort={onSort} />
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--ui-text-muted)", padding: "1.5rem" }}>
                        {rows.length > 0
                          ? "No employees match."
                          : `No employees were employed in ${year}.`}
                      </td>
                    </tr>
                  )}
                  {visible.map((r) => {
                    const ok = canTick(r);
                    const notes = rowNotes(r, year);
                    // The whole row reads unavailable when it cannot be ticked — except an
                    // unblocked "No approved run" row, which waits for the admin to type its amount.
                    const greyed = !ok && !(r.noApprovedRun && !r.blockReason);
                    return (
                      <tr
                        key={r.employeeId}
                        style={{
                          borderTop: "1px solid var(--ui-border-soft)",
                          ...(greyed ? { background: "var(--ui-surface-recessed)", opacity: 0.55 } : {}),
                        }}
                      >
                        <td style={{ ...td, textAlign: "left" }}>
                          <input
                            type="checkbox"
                            aria-label={`Include ${r.fullName}`}
                            checked={ok && ticked.has(r.employeeId)}
                            disabled={!ok}
                            onChange={() => toggle(r.employeeId)}
                          />
                        </td>
                        <td style={{ ...td, textAlign: "left" }}>
                          {/* Line 1: name + EMP id inline (the EmployeePicker format); line 2: the chips. */}
                          <div>
                            <span style={{ color: "var(--ui-text)" }}>{r.fullName}</span>{" "}
                            <span style={{ color: "var(--ui-text-muted)", fontSize: "0.6875rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                              EMP-{r.employeeId}
                            </span>
                          </div>
                          {notes.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem", marginTop: "0.25rem" }}>
                              {notes.map((n, i) => (
                                <PwPopover
                                  key={`${i}-${n.label}`}
                                  ariaLabel={n.label}
                                  strategy="fixed"
                                  trigger={<span style={{ ...chipStyle, ...CHIP_TONE[n.tone] }}>{n.label}</span>}
                                >
                                  {n.text}
                                </PwPopover>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={td}>{formatPeso(r.approvedBasis)}</td>
                        <td style={td}>{formatPeso(r.projected)}</td>
                        <td style={td}>{formatPeso(r.thirteenth)}</td>
                        <td style={td}>{formatPeso(r.alreadyGiven)}</td>
                        <td style={td}>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            aria-label={`To pay for ${r.fullName}`}
                            value={amounts[r.employeeId] ?? String(r.computedToPay)}
                            onChange={(e) => {
                              const typed = e.target.value;
                              setAmounts((prev) => ({ ...prev, [r.employeeId]: typed }));
                              // An invalid amount unticks the row for real, so fixing it later
                              // never silently re-ticks it.
                              if (!isTickable(r, Number(typed))) {
                                setTicked((prev) => {
                                  const next = new Set(prev);
                                  next.delete(r.employeeId);
                                  return next;
                                });
                              }
                            }}
                            style={{ ...inputStyle, width: "6rem", textAlign: "right" }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.75rem", marginTop: "1rem", flexWrap: "wrap" }}>
          {chosen.length > 0 && (
            <span style={{ fontSize: "0.8125rem", color: "var(--ui-text-secondary)", marginRight: "auto" }}>
              {`Creating ${chosen.length} ${chosen.length === 1 ? "entry" : "entries"} · ${formatPeso(total)}`}
            </span>
          )}
          <button onClick={onClose} disabled={saving} style={{ ...inputStyle, cursor: "pointer", background: "var(--ui-surface)" }}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || chosen.length === 0}
            style={{
              padding: "0.5rem 1rem",
              background: "var(--ui-primary)",
              color: "var(--ui-surface)",
              border: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.8125rem",
              cursor: saving ? "wait" : chosen.length === 0 ? "not-allowed" : "pointer",
              opacity: saving || chosen.length === 0 ? 0.7 : 1,
            }}
          >
            Create
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GenerateThirteenthMonthModal;
